import axios from 'axios';
import axiosRetry, { exponentialDelay } from 'axios-retry';
import {
  RETRY_COUNT,
  RETRY_DELAY_FACTOR_MS,
  shouldRetryRequest,
} from 'src/services/retryPolicy';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import {
  AxiosResponseWithRefreshToken,
  ContentTypeEnum,
} from 'src/models/GalenData/Request.model';

import { captureAxiosError } from 'src/utils/errorReporting';

const baseURL = import.meta.env.VITE_BASE_URL_GALEN_DATA;

const httpRequest = axios.create({
  baseURL,
  timeout: 10_000,
  headers: {
    'X-APP-TYPE': import.meta.env.VITE_X_APP_TYPE,
    'X-API-VERSION': import.meta.env.VITE_X_API_VERSION,
    'X-TENANT-DOMAIN': import.meta.env.VITE_X_TENANT_DOMAIN,
    'Content-Type': ContentTypeEnum.JSON,
  },
});

httpRequest.interceptors.request.use((config) => {
  const authToken = useRemoteBoundStore.getState().authInfo.authToken;

  if (authToken) {
    config.headers['Authorization'] = authToken;
  }

  return config;
});

httpRequest.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Report only once the request is done — axios-retry re-runs this on every attempt, and
    // the ceiling is per-request so a no-retry failure (0) is still reported, not swallowed.
    const maxRetries = error.config?.['axios-retry']?.retries ?? RETRY_COUNT;
    const retryCount = error.config?.['axios-retry']?.retryCount ?? 0;
    const willRetry = shouldRetryRequest(error) && retryCount < maxRetries;
    if (!willRetry) {
      captureAxiosError(error, {
        customMessage: 'HTTP Request Error',
        operation: 'HTTP Request',
        tags: {
          error_source: 'http_request_interceptor',
        },
      });
    }

    // 1. network error
    if (!error.response) {
      throw error;
    }

    const { response, config } = error;

    // 2. server error
    if (response?.status !== 401) {
      throw error;
    }

    const {
      authInfo: { authToken, refreshToken },
    } = useRemoteBoundStore.getState();

    // 3. refresh_token has been deleted
    if (!refreshToken) {
      // TODO: need to add AuthRouter
      // history.push('/login', { state: { from: history.location } });
      useRemoteBoundStore.getState().logOut();
      throw error;
    }

    // 4. if the auth_token has been expired, get new authorization token
    try {
      const res = await axios<void, AxiosResponseWithRefreshToken>({
        method: 'post',
        url: baseURL + '/auth/refresh-authtoken',
        headers: {
          'X-APP-TYPE': import.meta.env.VITE_X_APP_TYPE,
          'X-API-VERSION': import.meta.env.VITE_X_API_VERSION,
          'X-TENANT-DOMAIN': import.meta.env.VITE_X_TENANT_DOMAIN,
          'Content-Type': ContentTypeEnum.TEXT,
          Authorization: authToken,
        },
        data: refreshToken,
      });

      const newAuthToken = res.headers.authorization;
      const newRefreshToken = res.headers['x-refresh-token'];

      const { setAuth } = useRemoteBoundStore.getState();
      setAuth({
        authInfo: {
          authToken: newAuthToken,
          refreshToken: newRefreshToken,
        },
      });

      config.headers['Authorization'] = newAuthToken;
      return httpRequest(config);
    } catch (refreshError) {
      // 5. if get new token failed - capture this error too
      captureAxiosError(refreshError, {
        customMessage: 'Token Refresh Failed',
        operation: 'Token Refresh',
        tags: {
          error_source: 'http_request_interceptor',
          error_type: 'token_refresh_failed',
        },
      });
      useRemoteBoundStore.getState().logOut();
      // history.push('/login', { from: history.location });
      // Toast.info('Token expired', 1);
      throw refreshError;
    }
  },
);

// Registered after the response handler above so that handler runs first on each attempt
// (it owns error reporting and the 401 refresh).
axiosRetry(httpRequest, {
  retries: RETRY_COUNT,
  retryDelay: (retryCount, error) =>
    exponentialDelay(retryCount, error, RETRY_DELAY_FACTOR_MS),
  // Cap the whole retry chain (attempts + backoff) to the per-request timeout.
  shouldResetTimeout: false,
  retryCondition: shouldRetryRequest,
});

export default httpRequest;
